/**
 * Onboarding 2 étapes — compte parent obligatoire (alpha).
 *
 * Étape 1 — Nom de famille (input requis).
 * Étape 2 — **Compte administrateur parent** : prénom + email + mot de
 *           passe, tous **obligatoires**. On crée un vrai compte dès la
 *           première utilisation (plus de profil local anonyme).
 *
 * À la soumission de l'étape 2 :
 *   - On tente `registerAndUpdateSession` (création du compte serveur +
 *     session). Sur succès : `addHome` + `addMember(parent)` puis
 *     `syncHomesWithServer` pousse le foyer neuf.
 *   - **Dégradation local-only (§2.4)** : si le backend est injoignable
 *     (hors-ligne / pas encore déployé → erreur réseau), on crée quand
 *     même le foyer en local et on informe l'utilisateur (ton positif,
 *     §2.1) qu'il finalisera son compte en ligne depuis Réglages. Le
 *     foyer local se synchronisera à ce moment-là.
 *   - Erreur métier (email déjà pris…) : on bloque pour laisser
 *     corriger, sans créer de foyer.
 *
 * Le guard de redirection automatique (« no home → /onboarding ») vit
 * dans `mountAppShell` (cf. `AppShell.ts`).
 */
import { html, type TemplateResult } from 'lit-html';
import styles from './View.module.css';
import { getLang, t, type TKey } from '../../i18n/index.js';
import { addHome, addMember } from '../../stores/homes.js';
import { registerAndUpdateSession } from '../../stores/session.js';
import { syncHomesWithServer } from '../../stores/homes-sync.js';
import { ApiError } from '../../api/client.js';
import type { AuthLang } from '../../api/auth.js';
import { showToast } from '../../ui/Toast.js';
import { bumpUi } from '../../stores/ui.js';
import { navigate } from '../../lib/router.js';
import { nestorMark } from '../../ui/NestorMark.js';

interface OnboardingState {
  step: 1 | 2;
  familyName: string;
  familyError: boolean;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  /** Clé i18n de l'erreur affichée en bas de l'étape 2 (ou `null`). */
  authError: string | null;
  /** `true` pendant l'appel réseau — désactive le bouton créer. */
  submitting: boolean;
}

const state: OnboardingState = {
  step: 1,
  familyName: '',
  familyError: false,
  adminName: '',
  adminEmail: '',
  adminPassword: '',
  authError: null,
  submitting: false,
};

function setStep(step: 1 | 2): void {
  if (state.step === step) return;
  state.step = step;
  state.familyError = false;
  state.authError = null;
  bumpUi();
}

/** Validation client (feedback immédiat) — miroir de `account.ts`. */
function validateStep2(): string | null {
  if (!state.adminName.trim()) return 'onb_error_name';
  if (!state.adminEmail.includes('@')) return 'account_err_invalid_email';
  if (state.adminPassword.length < 8) return 'account_err_password_too_short';
  return null;
}

/** Backend injoignable (hors-ligne / pas déployé) vs erreur métier. */
function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof ApiError && err.status === 503) return true;
  return false;
}

/** Mappe une erreur API vers une clé i18n bienveillante (§2.1). */
function authErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'email_taken') return 'account_err_email_taken';
    if (err.code === 'invalid_credentials') return 'account_err_invalid_credentials';
  }
  return 'account_err_generic';
}

function resetOnboardingState(): void {
  state.step = 1;
  state.familyName = '';
  state.familyError = false;
  state.adminName = '';
  state.adminEmail = '';
  state.adminPassword = '';
  state.authError = null;
  state.submitting = false;
}

function step1Template(): TemplateResult {
  const onContinue = (e?: Event): void => {
    e?.preventDefault();
    const v = state.familyName.trim();
    if (!v) {
      state.familyError = true;
      bumpUi();
      return;
    }
    state.familyName = v;
    setStep(2);
  };
  return html`<form class=${styles.card} data-testid="onb-step1" @submit=${onContinue}>
    <h2 class=${styles.title}>${t('onb_step1_title')}</h2>
    <div class=${styles.sub}>${t('onb_step1_sub')}</div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('onb_label_family')}</label>
      <input
        type="text"
        class=${`${styles.formInput} ${state.familyError ? styles.error : ''}`}
        data-testid="onb-family"
        placeholder=${t('onb_placeholder_family')}
        .value=${state.familyName}
        @input=${(e: Event): void => {
          state.familyName = (e.target as HTMLInputElement).value;
          if (state.familyError && state.familyName.trim()) {
            state.familyError = false;
            bumpUi();
          }
        }}
        autocomplete="family-name"
      />
      <div
        class=${`${styles.errorMsg} ${state.familyError ? styles.show : ''}`}
        data-testid="onb-family-err"
      >
        ${t('onb_error_family')}
      </div>
    </div>
    <button type="submit" class=${styles.btnPrimary} data-testid="onb-next">
      ${t('onb_btn_continue')}
    </button>
    <button
      type="button"
      class=${styles.btnBack}
      data-testid="onb-to-login"
      @click=${(): void => {
        navigate('/login');
      }}
    >
      ${t('onb_have_account')}
    </button>
  </form>`;
}

function step2Template(): TemplateResult {
  const onCreate = (e?: Event): void => {
    e?.preventDefault();
    if (state.submitting) return;
    const validationError = validateStep2();
    if (validationError) {
      state.authError = validationError;
      bumpUi();
      return;
    }
    state.authError = null;
    state.submitting = true;
    bumpUi();

    void (async (): Promise<void> => {
      const familyName = state.familyName.trim();
      const name = state.adminName.trim();
      const email = state.adminEmail.trim();
      const password = state.adminPassword;
      let degraded = false;
      try {
        await registerAndUpdateSession({
          email,
          password,
          displayName: name,
          lang: getLang() as AuthLang,
        });
      } catch (err) {
        if (isNetworkError(err)) {
          // §2.4 : backend absent/injoignable → foyer créé en local, le
          // compte sera finalisé en ligne depuis Réglages plus tard.
          degraded = true;
        } else {
          // Erreur métier (email déjà pris…) → bloquer pour corriger.
          state.authError = authErrorKey(err);
          state.submitting = false;
          bumpUi();
          return;
        }
      }

      // Reset AVANT les mutations : le re-render synchrone déclenché par
      // `addHome` réutilise l'état déjà nettoyé (pas de valeurs fantômes).
      resetOnboardingState();
      const home = addHome(familyName, 'classic');
      if (home) addMember(home.id, name, 'parent');
      // Compte serveur OK → pousse le foyer neuf (sync idempotente).
      if (!degraded && home) void syncHomesWithServer();
      if (degraded) {
        showToast({ message: t('onb_offline_local'), kind: 'success' });
      }
      // Défère la navigation au microtask suivant : garantit que les
      // diffs lit-html de `addHome`/`addMember` sont terminés avant le
      // changement de route (sinon le pushState peut être avalé).
      queueMicrotask(() => {
        navigate('/');
      });
    })();
  };
  const onBack = (): void => {
    setStep(1);
  };
  const onInput =
    (field: 'adminName' | 'adminEmail' | 'adminPassword') =>
    (e: Event): void => {
      state[field] = (e.target as HTMLInputElement).value;
      if (state.authError) {
        state.authError = null;
        bumpUi();
      }
    };
  return html`<form class=${styles.card} data-testid="onb-step2" @submit=${onCreate}>
    <h2 class=${styles.title}>${t('onb_step2_title')}</h2>
    <div class=${styles.sub}>${t('onb_step2_sub')}</div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('onb_label_name')}</label>
      <input
        type="text"
        class=${styles.formInput}
        data-testid="onb-name"
        placeholder=${t('onb_placeholder_name')}
        .value=${state.adminName}
        @input=${onInput('adminName')}
        autocomplete="given-name"
      />
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('account_email_label')}</label>
      <input
        type="email"
        class=${styles.formInput}
        data-testid="onb-email"
        placeholder="prenom@exemple.com"
        .value=${state.adminEmail}
        @input=${onInput('adminEmail')}
        autocomplete="email"
      />
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('account_password_label')}</label>
      <input
        type="password"
        class=${styles.formInput}
        data-testid="onb-password"
        minlength="8"
        .value=${state.adminPassword}
        @input=${onInput('adminPassword')}
        autocomplete="new-password"
      />
      <div class=${styles.hint}>${t('account_password_hint')}</div>
    </div>
    <div
      class=${`${styles.errorMsg} ${state.authError ? styles.show : ''}`}
      data-testid="onb-auth-err"
      role="alert"
    >
      ${state.authError ? t(state.authError as TKey) : ''}
    </div>
    <button
      type="submit"
      class=${styles.btnPrimary}
      ?disabled=${state.submitting}
      data-testid="onb-create"
    >
      ${t('onb_btn_create')}
    </button>
    <button type="button" class=${styles.btnBack} data-testid="onb-back" @click=${onBack}>
      ${t('onb_btn_back')}
    </button>
  </form>`;
}

export function renderOnboardingView(): TemplateResult {
  return html`<div class=${styles.screen} data-testid="view-onboarding">
    <div class=${styles.inner}>
      <div class=${styles.logo}>${nestorMark({ size: 28 })}</div>
      <div class=${styles.tagline}>${t('onb_tagline')}</div>
      <div class=${styles.stepIndicator}>${t('onb_step_label')} ${state.step.toString()} / 2</div>
      <div class=${styles.progress}>
        <span
          class=${styles.progressFill}
          style="width: ${state.step === 1 ? '50%' : '100%'};"
        ></span>
      </div>
      ${state.step === 1 ? step1Template() : step2Template()}
      <div class=${styles.footer}>ALPHA 1.0</div>
    </div>
  </div>`;
}

/** @internal pour tests : reset l'état module-local. */
export function _resetOnboardingForTests(): void {
  resetOnboardingState();
}
